import React, { useMemo, useState } from 'react'

import { Button, TableContainer, Table, Thead, Tbody, Tr, Th, Text } from '@chakra-ui/react'
import AddOnRow from './AddOnRow'
import CreateRentalAddOnModal from './CreateRentalAddOnModal'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { useRentRollAddOnsDataQuery } from 'graphql/generated'
import type { RentRollAddOnsDataQuery } from 'graphql/generated'

export type RentRollAddOn = NonNullable<
  RentRollAddOnsDataQuery['rentals_by_pk']
>['rental_add_ons'][number]

export default function AddOns({
  onMonetaryDataChange,
  rentalId
}: {
  onMonetaryDataChange?: () => void
  rentalId: string
}) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const queryContext = useMemo(() => ({ suspense: false }), [])
  const [{ data, fetching, error }, refetchAddOns] = useRentRollAddOnsDataQuery({
    variables: { rentalId },
    requestPolicy: 'cache-and-network',
    context: queryContext
  })
  const rental = data?.rentals_by_pk

  const refreshAddOns = () => {
    refetchAddOns({ requestPolicy: 'network-only' })
    onMonetaryDataChange?.()
  }

  if (fetching && !rental) {
    return (
      <ContentSectionCard collapsable={false} title={'Add-Ons'}>
        <Text color='gray.500'>Loading add-ons...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !rental) {
    return (
      <ContentSectionCard collapsable={false} title={'Add-Ons'}>
        <Text color='red.600'>Unable to load add-ons.</Text>
      </ContentSectionCard>
    )
  }

  if (!rental) {
    return null
  }

  if (fetching && !Array.isArray(rental.rental_add_ons)) {
    return (
      <ContentSectionCard collapsable={false} title={'Add-Ons'}>
        <Text color='gray.500'>Loading add-ons...</Text>
      </ContentSectionCard>
    )
  }

  if (error && !Array.isArray(rental.rental_add_ons)) {
    return (
      <ContentSectionCard collapsable={false} title={'Add-Ons'}>
        <Text color='red.600'>Unable to load add-ons.</Text>
      </ContentSectionCard>
    )
  }

  if (!Array.isArray(rental.rental_add_ons)) {
    return null
  }

  const addOns = rental.rental_add_ons

  return (
    <ContentSectionCard
      title={'Add-Ons'}
      collapsable={false}
      action={
        <Button size='xs' colorScheme={'teal'} onClick={() => setIsModalOpen(true)}>
          + Add-On
        </Button>
      }
    >
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Add-On</Th>
              <Th>Price</Th>
              <Th>Start Date</Th>
              <Th>End Date</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {addOns.map((addOn) => (
              <AddOnRow
                addOn={addOn}
                key={addOn.id}
                occupancyDate={rental.occupancy_date}
                onSuccess={refreshAddOns}
              />
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <CreateRentalAddOnModal
        rentalId={rental.id}
        occupancyDate={rental.occupancy_date}
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        onSuccess={refreshAddOns}
      />
    </ContentSectionCard>
  )
}
